'use client';
import type { ChangeEvent, RefObject } from 'react';

import { FieldGroup } from '@/app/components/forms/field-group';
import {
  getCustomFormStyle,
  inputBoxStyled,
  inputIconStyled,
  inputTextStyled,
} from '@/app/components/forms/styles';
import { type TextFieldProps } from '@/app/components/forms/text-field';
import { Icon } from '@/app/components/icons';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { IconSearch } from '@/app/components/icons/ui-actions/icon-search';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

type TextInputProps = Omit<
  TextFieldProps,
  | 'iconBefore'
  | 'iconAfter'
  | 'onChange'
  | 'prefix'
  | 'suffix'
  | 'type'
  | 'spinButton'
  | 'spinUpDisabled'
  | 'spinDownDisabled'
  | 'onSpinUp'
  | 'onSpinDown'
>;

export interface SearchInputProps extends TextInputProps {
  onChange?: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  onClear?: () => void;
}

export const SearchInput = ({
  className,
  inputClassName,
  label,
  secondaryLabel,
  errorMessage,
  message,
  theme = 'light',
  showMaxLength = false,
  disabled = false,
  required = false,
  error = false,
  value,
  name,
  maxLength,
  onChange,
  customStyle,
  inputRef,
  ref,
  onClear,
  ...rest
}: SearchInputProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled || (value && maxLength && value.length > maxLength)) return;
    safeCall(onChange, event.target.value);
  };

  const handleClear = () => {
    safeCall(onClear);
    safeCall(onChange, '');
  };

  const wrapperClass = cn(
    inputBoxStyled({
      theme,
      error,
      disabled,
    }),
    'group flex h-12 cursor-text items-center gap-x-2 rounded-sm px-4',
    getCustomFormStyle(customStyle?.textfield?.wrapper, { error, disabled }),
    className
  );

  const inputClass = cn(
    'webkit-autofill-text-gray-900 h-full',
    inputTextStyled({
      theme,
      error,
      disabled,
    }),
    getCustomFormStyle(customStyle?.textfield?.text, { error, disabled }),
    inputClassName
  );

  const iconClass = cn(
    inputIconStyled({
      theme,
      error,
      disabled,
    }),
    getCustomFormStyle(customStyle?.textfield?.icon, { error, disabled })
  );

  const clearIconClass = cn(
    inputIconStyled({
      theme,
      disabled,
    }),
    'pointer-events-auto cursor-pointer',
    getCustomFormStyle(customStyle?.textfield?.icon, { disabled })
  );

  return (
    <FieldGroup
      htmlFor={name}
      label={label}
      secondaryLabel={secondaryLabel}
      required={required}
      error={error}
      errorMessage={errorMessage}
      message={message}
      theme={theme}
      customStyle={customStyle?.fieldGroup}
      length={value?.length}
      maxLength={maxLength}
      showMaxLength={showMaxLength}
      disabled={disabled}
      ref={label ? ref : undefined}
    >
      <div className={wrapperClass} ref={label ? undefined : ref}>
        <Icon className={iconClass} icon={<IconSearch />} />
        <input
          ref={inputRef}
          type="text"
          id={name}
          name={name}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          onChange={handleChange}
          className={inputClass}
          {...createDataAttributes({
            test: `search-input-${name}`,
            autofocus: true,
          })}
          {...rest}
        />
        {!disabled && (value || '')?.length > 0 && (
          <button
            className={clearIconClass}
            onClick={handleClear}
            {...createDataAttributes({ test: `clear-button` })}
          >
            <Icon icon={<IconClose />} />
          </button>
        )}
      </div>
    </FieldGroup>
  );
};
